import React, { useState } from "react";

import { AcknowledgedPolicyEnum } from "@/core/constants";
import {
  Button,
  ButtonTypes,
  Card,
  ErrorCard,
  Image,
  Text,
  TextColors,
  TextTypes,
} from "@/component-library";
import { createAcknowledgementV2 } from "@/utils/acknowledgementUtils";
import { CreditCardAgreementScrollableTextModalHOC } from "@/component-library/hocs/CreditCardAgreementScrollableTextModalHOC";
import { getAutomationDataAttr } from "@/utils/automationUtils";
import { hideLoader, showLoader } from "@/redux/actions/loader";
import { patientRequests } from "@/api/(requests)/patient";
import { preventModalPageScroll } from "@/component-library/ScrollableTextModal";
import store from "@/redux/store";

const CardSection = ({
  captchaCompleted,
  captchaComponent,
  errorMessage,
  expirationDate,
  isExpired,
  lastFourDigits,
  onAddCardButtonClick,
  onReplaceCardButtonClick,
  profile,
  resetCaptcha,
}) => {
  const [showModal, setShowModal] = useState(false);

  const closeModal = () => {
    setShowModal(false);
    resetCaptcha();
    preventModalPageScroll(false);
  };

  const onCreditCardTncSubmit = async () => {
    store.dispatch(showLoader());
    const data = {
      acknowledgements: {
        ...profile.acknowledgements,
        [AcknowledgedPolicyEnum.CREDIT_CARD_AUTHORIZATION_POLICY_NAME]: createAcknowledgementV2({
          consentResponse: true,
        }),
      },
      username: profile.username,
    };

    store.dispatch(hideLoader());
    await patientRequests.update_profile({ data });
    closeModal();
    lastFourDigits ? onReplaceCardButtonClick() : onAddCardButtonClick();
  };

  return (
    <>
      <CreditCardAgreementScrollableTextModalHOC
        captchaCompleted={captchaCompleted}
        captchaComponent={captchaComponent}
        onCancel={closeModal}
        onSubmit={onCreditCardTncSubmit}
        show={showModal}
      />
      <Card className="CardSection">
        <Text className="CardSection__title" color={TextColors.black} type={TextTypes.heading2}>
          Card on File
        </Text>
        <div className="CardSection__content">
          <Text className="CardSection__title" color={TextColors.grey} type={TextTypes.paragraph}>
            This card will be used to process payments such as copayments for sessions and
            deductibles.
          </Text>
          {errorMessage && (
            <ErrorCard className="CardSection__error">
              <Text color={TextColors.red} type={TextTypes.subtitle}>
                {errorMessage}
              </Text>
            </ErrorCard>
          )}
          {lastFourDigits ? (
            <div className="CardSection__card-info">
              <div className="CardSection__card-img--container">
                <Image
                  alt="Credit Card"
                  className="CardSection__card-img--exists"
                  src="credit-card-md.svg"
                />
              </div>
              <div className="CardSection__card-info--container">
                <Text className="CardSection__number" type={TextTypes.subtitle}>
                  Card ending in {lastFourDigits}
                </Text>
                <Text
                  className={`CardSection__expiration${isExpired ? " expired" : ""}`}
                  color={isExpired ? TextColors.red : TextColors.black}
                  type={TextTypes.paragraph}
                >
                  Expires {expirationDate.format("MM/YYYY")}
                </Text>
              </div>
            </div>
          ) : (
            <div className="CardSection__card-info--empty">
              <Image
                alt="Credit Card"
                className="CardSection__card-img--empty"
                src="credit-card-lg.svg"
              />
              <Text color={TextColors.grey} type={TextTypes.subtitle}>
                Please add a new card
              </Text>
            </div>
          )}
          <div className="CardSection__button--container">
            <Button
              buttonType={ButtonTypes.primaryV2}
              onClick={() => {
                window.grecaptcha.reset();
                preventModalPageScroll(true);
                setShowModal(true);
              }}
              {...getAutomationDataAttr("CreditCardAddUpdateButton")}
            >
              <Image
                alt="Credit Card"
                className="CardSection__card-img--icon"
                src="credit-card-icon.svg"
              />
              {lastFourDigits ? "Replace Card" : "Add Card"}
            </Button>
          </div>
        </div>
      </Card>
    </>
  );
};

export default CardSection;
